import React, { useEffect, useState } from "react";
import TopBar from "@/components/TopBar";
import VerifiedOrgBadge from "@/components/VerifiedOrgBadge";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { motion } from "framer-motion";
import { Save, Check, User, Mail, Globe, Phone, MapPin, ScrollText, Building2, Briefcase, Bell, Send, Zap, CreditCard, AlertTriangle, RotateCcw, Gift, Sparkles, Heart, Pause, Play } from "lucide-react";
import { BRANCH_META } from "@/lib/branches";
import { Link } from "react-router-dom";

export default function Settings() {
    const { user, refresh } = useAuth();
    const [form, setForm] = useState({ name: "", bio: "", location: "", website: "", phone: "" });
    const [busy, setBusy] = useState(false);
    const [saved, setSaved] = useState(false);
    const [error, setError] = useState(null);

    // --- Org conversion state ---
    const [orgForm, setOrgForm] = useState({ org_name: "", org_domain: "", org_website: "", org_description: "" });
    const [orgBusy, setOrgBusy] = useState(false);
    const [orgSaved, setOrgSaved] = useState(false);
    const [orgError, setOrgError] = useState(null);

    useEffect(() => {
        if (user) {
            setForm({
                name: user.name || "",
                bio: user.bio || "",
                location: user.location || "",
                website: user.website || "",
                phone: user.phone || "",
            });
            setOrgForm({
                org_name: user.org_name || "",
                org_domain: user.org_domain || "",
                org_website: user.org_website || "",
                org_description: user.org_description || "",
            });
        }
    }, [user]);

    const submit = async (e) => {
        e.preventDefault();
        setBusy(true);
        setError(null);
        try {
            await api.patch("/profile/me", form);
            await refresh();
            setSaved(true);
            setTimeout(() => setSaved(false), 2000);
        } catch (err) {
            setError(err.response?.data?.detail || "Could not save your profile");
        } finally {
            setBusy(false);
        }
    };

    const submitOrg = async (e) => {
        e.preventDefault();
        setOrgBusy(true);
        setOrgError(null);
        try {
            await api.post("/org/convert", orgForm);
            await refresh();
            setOrgSaved(true);
            setTimeout(() => setOrgSaved(false), 2500);
        } catch (err) {
            setOrgError(err.response?.data?.detail || "Could not save organization");
        } finally {
            setOrgBusy(false);
        }
    };

    // --- Email preferences ---
    const [digestBusy, setDigestBusy] = useState(false);
    const [digestSent, setDigestSent] = useState(false);
    const toggleDigest = async (optOut) => {
        setDigestBusy(true);
        try {
            await api.post("/emails/digest/opt", { opt_out: optOut });
            await refresh();
        } finally {
            setDigestBusy(false);
        }
    };
    const sendTestDigest = async () => {
        setDigestBusy(true);
        try {
            await api.post("/emails/digest/send-to-me");
            setDigestSent(true);
            setTimeout(() => setDigestSent(false), 3200);
        } finally {
            setDigestBusy(false);
        }
    };
    const toggleJobAlerts = async (optIn) => {
        setDigestBusy(true);
        try {
            await api.post("/emails/job-alerts/opt", { opt_in: optIn });
            await refresh();
        } finally {
            setDigestBusy(false);
        }
    };

    // --- Billing / subscription state ---
    const [billing, setBilling] = useState(null);
    const [referrals, setReferrals] = useState(null);
    const [billingBusy, setBillingBusy] = useState(false);
    const [billingError, setBillingError] = useState(null);
    const [confirmCancel, setConfirmCancel] = useState(false);
    // Cancel-reason survey — required before the final "confirm cancel" click.
    const [cancelReason, setCancelReason] = useState(null);
    const [cancelDetail, setCancelDetail] = useState("");
    // Pause flow — a small chooser between the "keep exploring" and cancel actions.
    const [pauseOpen, setPauseOpen] = useState(false);

    useEffect(() => {
        if (!user) return;
        api.get("/billing/status").then((r) => setBilling(r.data)).catch(() => {});
        api.get("/referrals/me").then((r) => setReferrals(r.data)).catch(() => {});
    }, [user?.id]);

    const doCancel = async () => {
        setBillingBusy(true);
        setBillingError(null);
        try {
            const { data } = await api.post("/billing/cancel-subscription", {
                reason: cancelReason,
                detail: cancelReason === "other" ? cancelDetail.trim().slice(0, 500) : null,
            });
            setBilling((b) => ({ ...b, ...data, cancel_at_period_end: true }));
            setConfirmCancel(false);
            setCancelReason(null);
            setCancelDetail("");
            await refresh();
        } catch (e) {
            setBillingError(e.response?.data?.detail || "Could not cancel");
        } finally {
            setBillingBusy(false);
        }
    };
    const doResume = async () => {
        setBillingBusy(true);
        try {
            await api.post("/billing/resume-subscription");
            const { data } = await api.get("/billing/status");
            setBilling(data);
            await refresh();
        } catch (e) {
            setBillingError(e.response?.data?.detail || "Could not resume");
        } finally {
            setBillingBusy(false);
        }
    };
    const doPause = async (months) => {
        setBillingBusy(true);
        setBillingError(null);
        try {
            await api.post("/billing/pause-subscription", { months });
            const { data } = await api.get("/billing/status");
            setBilling(data);
            setPauseOpen(false);
            setConfirmCancel(false);
            await refresh();
        } catch (e) {
            setBillingError(e.response?.data?.detail || "Could not pause");
        } finally {
            setBillingBusy(false);
        }
    };
    const doUnpause = async () => {
        setBillingBusy(true);
        setBillingError(null);
        try {
            await api.post("/billing/unpause-subscription");
            const { data } = await api.get("/billing/status");
            setBilling(data);
            await refresh();
        } catch (e) {
            setBillingError(e.response?.data?.detail || "Could not unpause");
        } finally {
            setBillingBusy(false);
        }
    };

    if (!user) return null;

    return (
        <div className="min-h-screen w-full">
            <TopBar />
            <div className="max-w-3xl mx-auto pt-24 pb-16 px-4 sm:px-8">
                <p className="chip" style={{ background: BRANCH_META.humanities.soft, color: BRANCH_META.humanities.text }}>
                    <User className="w-3 h-3" /> Account settings
                </p>
                <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tighter text-slate-900 mt-2 leading-none">
                    Your public presence.
                </h1>
                <p className="text-slate-600 mt-2">
                    This is what other Explorers, mentors, and quest sponsors see on your public
                    ledger at{" "}
                    <Link
                        className="font-mono text-humanities font-bold"
                        to={`/u/${user.username}`}
                    >
                        /u/{user.username}
                    </Link>
                </p>

                <motion.form
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    onSubmit={submit}
                    className="crystal rounded-3xl p-6 sm:p-8 mt-6 space-y-5"
                >
                    <Field
                        icon={<User className="w-4 h-4" />}
                        label="Display name"
                        required
                        value={form.name}
                        onChange={(v) => setForm({ ...form, name: v })}
                        testid="settings-name"
                        maxLength={64}
                        hint="Shown on your Living Resume Ledger and to every discipline roster."
                    />
                    <Field
                        icon={<Mail className="w-4 h-4" />}
                        label="Email"
                        value={user.email}
                        readOnly
                        testid="settings-email"
                        hint="Locked. Contact support to change."
                    />
                    <Field
                        icon={<ScrollText className="w-4 h-4" />}
                        label="Bio"
                        value={form.bio}
                        onChange={(v) => setForm({ ...form, bio: v })}
                        testid="settings-bio"
                        maxLength={280}
                        textarea
                        hint={`${form.bio.length}/280 · One or two sentences about what you're building.`}
                    />
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                        <Field
                            icon={<MapPin className="w-4 h-4" />}
                            label="Location"
                            value={form.location}
                            onChange={(v) => setForm({ ...form, location: v })}
                            testid="settings-location"
                            maxLength={64}
                            placeholder="City, Country"
                        />
                        <Field
                            icon={<Phone className="w-4 h-4" />}
                            label="Phone (optional)"
                            value={form.phone}
                            onChange={(v) => setForm({ ...form, phone: v })}
                            testid="settings-phone"
                            maxLength={40}
                            placeholder="+1 555 123 4567"
                            hint="Never public. Only used for critical account safety."
                        />
                    </div>
                    <Field
                        icon={<Globe className="w-4 h-4" />}
                        label="Website"
                        value={form.website}
                        onChange={(v) => setForm({ ...form, website: v })}
                        testid="settings-website"
                        maxLength={200}
                        placeholder="https://your-site.com"
                    />

                    {error && (
                        <div className="text-sm text-white bg-sciences px-3 py-2 rounded-xl" data-testid="settings-error">
                            {error}
                        </div>
                    )}

                    <div className="flex items-center justify-end gap-3">
                        {saved && (
                            <span className="text-sm text-trades font-display font-bold flex items-center gap-1">
                                <Check className="w-4 h-4" /> Saved
                            </span>
                        )}
                        <button
                            disabled={busy}
                            className="arcade-btn"
                            data-variant="primary"
                            data-testid="settings-save"
                            type="submit"
                        >
                            <Save className="w-4 h-4" /> {busy ? "Saving…" : "Save changes"}
                        </button>
                    </div>
                </motion.form>

                {/* --- Resume editor entry point --- */}
                <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="crystal rounded-3xl p-6 sm:p-8 mt-8 flex flex-wrap items-center justify-between gap-4"
                    data-testid="settings-resume-cta"
                >
                    <div className="min-w-0">
                        <h2 className="font-display font-black text-2xl tracking-tight text-slate-900">
                            Traditional resume fields
                        </h2>
                        <p className="text-sm text-slate-600 mt-1 max-w-lg">
                            Add Education, Work Experience, Leadership, and Skills so hiring
                            managers see the whole picture alongside your Verified Ledger.
                        </p>
                    </div>
                    <Link
                        to="/settings/resume"
                        className="arcade-btn"
                        data-variant="primary"
                        data-testid="settings-open-resume-editor"
                    >
                        Edit resume →
                    </Link>
                </motion.div>

                {/* --- Veteran verification CTA --- */}
                <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="crystal rounded-3xl p-6 sm:p-8 mt-4 flex flex-wrap items-center justify-between gap-4"
                    style={{ background: "linear-gradient(135deg, #FFF7ED, #FEF3C7)" }}
                    data-testid="settings-vet-cta"
                >
                    <div className="min-w-0">
                        <h2 className="font-display font-black text-2xl tracking-tight text-amber-900">
                            Veterans — 50% off Atlas
                        </h2>
                        <p className="text-sm text-amber-800 mt-1 max-w-lg">
                            Upload your service record, redact anything you want private, and
                            we'll apply a permanent 50% discount to your Atlas subscription.
                        </p>
                    </div>
                    <Link
                        to="/settings/verify-vet"
                        className="arcade-btn"
                        data-variant="primary"
                        data-testid="settings-open-verify-vet"
                    >
                        Get 50% off →
                    </Link>
                </motion.div>

                {/* --- Email preferences --- */}
                <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="crystal rounded-3xl p-6 sm:p-8 mt-8 space-y-4"
                    data-testid="email-prefs-section"
                >
                    <div className="flex flex-wrap items-center gap-2">
                        <p className="chip" style={{ background: BRANCH_META.humanities.soft, color: BRANCH_META.humanities.text }}>
                            <Bell className="w-3 h-3" /> Emails
                        </p>
                        {user.email_digest_opt_out ? (
                            <span className="chip bg-slate-100 text-slate-600">Unsubscribed</span>
                        ) : (
                            <span className="chip bg-emerald-50 text-emerald-800">Subscribed</span>
                        )}
                    </div>
                    <h2 className="font-display font-black text-2xl sm:text-3xl tracking-tighter text-slate-900 leading-none">
                        Weekly Atlas digest
                    </h2>
                    <p className="text-sm text-slate-600">
                        Every Monday: new roles + bounties + peers in your beacon disciplines, and an AI-suggested next micro-quest.
                        Sent to <span className="font-mono">{user.email}</span>.
                    </p>
                    <div className="flex flex-wrap items-center gap-3 pt-2">
                        {user.email_digest_opt_out ? (
                            <button
                                onClick={() => toggleDigest(false)}
                                disabled={digestBusy}
                                className="arcade-btn"
                                data-variant="primary"
                                data-testid="digest-subscribe"
                            >
                                <Bell className="w-4 h-4" /> Turn on weekly digest
                            </button>
                        ) : (
                            <button
                                onClick={() => toggleDigest(true)}
                                disabled={digestBusy}
                                className="arcade-btn"
                                data-variant="ghost"
                                data-testid="digest-unsubscribe"
                            >
                                Unsubscribe
                            </button>
                        )}
                        <button
                            onClick={sendTestDigest}
                            disabled={digestBusy || user.email_digest_opt_out}
                            className="arcade-btn"
                            data-variant="ghost"
                            data-testid="digest-send-test"
                        >
                            <Send className="w-4 h-4" />
                            {digestSent ? "Sent — check your inbox!" : "Send me a test digest"}
                        </button>
                        {(user.role === "admin" || user.email === "purposelyy@gmail.com") && (
                            <Link
                                to="/admin/emails"
                                className="arcade-btn"
                                data-variant="ghost"
                                data-testid="admin-emails-link"
                            >
                                📊 View analytics
                            </Link>
                        )}
                    </div>

                    {/* Instant job alerts — always-on funnel */}
                    <div className="mt-5 pt-5 border-t border-white/60" data-testid="job-alerts-block">
                        <div className="flex items-center gap-2">
                            <Zap className="w-4 h-4" color={BRANCH_META.trades.color} />
                            <h3 className="font-display font-bold text-lg text-slate-900">Instant role alerts</h3>
                            {user.job_alerts_opt_in && <span className="chip bg-emerald-50 text-emerald-800">On</span>}
                        </div>
                        <p className="text-sm text-slate-600 mt-1.5">
                            The moment a new role matching your beacons or master branches goes live, we email you — not just in the weekly digest.
                        </p>
                        <div className="mt-3">
                            {user.job_alerts_opt_in ? (
                                <button
                                    onClick={() => toggleJobAlerts(false)}
                                    disabled={digestBusy}
                                    className="arcade-btn"
                                    data-variant="ghost"
                                    data-testid="job-alerts-off"
                                >
                                    Turn off instant alerts
                                </button>
                            ) : (
                                <button
                                    onClick={() => toggleJobAlerts(true)}
                                    disabled={digestBusy || user.email_digest_opt_out}
                                    className="arcade-btn"
                                    data-variant="primary"
                                    data-testid="job-alerts-on"
                                >
                                    <Zap className="w-4 h-4" /> Turn on instant alerts
                                </button>
                            )}
                        </div>
                    </div>
                </motion.div>

                {/* --- Billing / Subscription --- */}
                <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="crystal rounded-3xl p-6 sm:p-8 mt-8 space-y-4"
                    data-testid="billing-section"
                >
                    <div className="flex flex-wrap items-center gap-2">
                        <p className="chip" style={{ background: BRANCH_META.trades.soft, color: BRANCH_META.trades.text }}>
                            <CreditCard className="w-3 h-3" /> Billing
                        </p>
                        {billing?.subscription_status === "active" && !billing?.cancel_at_period_end && (
                            <span className="chip bg-emerald-50 text-emerald-800">Active</span>
                        )}
                        {billing?.cancel_at_period_end && (
                            <span className="chip bg-amber-100 text-amber-800">Ending soon</span>
                        )}
                        {billing?.subscription_status !== "active" && (
                            <span className="chip bg-slate-100 text-slate-600">{billing?.subscription_status || "inactive"}</span>
                        )}
                    </div>
                    <h2 className="font-display font-black text-2xl sm:text-3xl tracking-tighter text-slate-900 leading-none">
                        Subscription
                    </h2>

                    {!billing && <p className="text-sm text-slate-500">Loading…</p>}

                    {billing && billing.referral_free_until && (
                        <div className="text-sm text-slate-600 rounded-2xl px-3 py-2 bg-emerald-50 text-emerald-800" data-testid="billing-referral-credit">
                            🎁 You have referral credit until{" "}
                            <b>{new Date(billing.referral_free_until).toLocaleDateString()}</b>
                            . Your subscription is essentially free until then.
                        </div>
                    )}

                    {billing && !billing.stripe_subscription_id && billing.subscription_status !== "active" && (
                        <div className="text-sm text-slate-600">
                            You don't have an active paid subscription right now.
                            {billing.has_access && " Your access is coming from your trial or referral credit."}
                        </div>
                    )}

                    {billing && !billing.stripe_subscription_id && billing.subscription_status === "active" && (
                        <div className="text-sm text-slate-600" data-testid="billing-managed">
                            Your access is managed by Atlas (admin-granted or via referral credit) — no self-serve billing controls needed. You're not being charged.
                        </div>
                    )}

                    {billing?.stripe_subscription_id && billing.current_period_end && !billing.paused && (
                        <div className="text-sm text-slate-600" data-testid="billing-period-end">
                            {billing.cancel_at_period_end
                                ? `Your subscription will end on ${new Date(billing.current_period_end).toLocaleDateString()}. You'll keep full access until then.`
                                : `Your next billing date is ${new Date(billing.current_period_end).toLocaleDateString()}.`}
                        </div>
                    )}

                    {billing?.paused && billing.pause_resumes_at && (
                        <div className="text-sm rounded-2xl px-3 py-2 bg-blue-50 text-blue-900 flex items-start gap-2" data-testid="billing-paused-banner">
                            <Pause className="w-4 h-4 flex-shrink-0 mt-0.5" />
                            <div>
                                Your subscription is <b>paused</b> until{" "}
                                <b>{new Date(billing.pause_resumes_at).toLocaleDateString()}</b>.
                                No charges during this window. Atlas access resumes automatically
                                on that date — or unpause now to return sooner.
                            </div>
                        </div>
                    )}

                    {billingError && (
                        <div className="text-sm text-white bg-sciences px-3 py-2 rounded-xl" data-testid="billing-error">{billingError}</div>
                    )}

                    {billing?.stripe_subscription_id && billing.paused && (
                        <button
                            onClick={doUnpause}
                            disabled={billingBusy}
                            className="arcade-btn"
                            data-variant="primary"
                            data-testid="unpause-subscription"
                        >
                            <Play className="w-4 h-4" /> Unpause now
                        </button>
                    )}

                    {billing?.stripe_subscription_id && !billing.cancel_at_period_end && !billing.paused && !confirmCancel && !pauseOpen && (
                        <div className="flex flex-wrap gap-2">
                            <button
                                onClick={() => setPauseOpen(true)}
                                className="arcade-btn"
                                data-variant="ghost"
                                data-testid="pause-subscription-open"
                            >
                                <Pause className="w-4 h-4" /> Pause subscription
                            </button>
                            <button
                                onClick={() => setConfirmCancel(true)}
                                className="arcade-btn"
                                data-variant="ghost"
                                data-testid="cancel-subscription-open"
                            >
                                Cancel subscription
                            </button>
                        </div>
                    )}

                    {pauseOpen && !billing?.paused && (
                        <div className="rounded-2xl border border-blue-200 bg-gradient-to-br from-blue-50 to-white p-5 space-y-3" data-testid="pause-chooser">
                            <div className="flex items-start gap-3">
                                <div className="w-10 h-10 rounded-2xl flex items-center justify-center flex-shrink-0" style={{ background: "#DBEAFE" }}>
                                    <Pause className="w-5 h-5" color="#1D4ED8" />
                                </div>
                                <div className="min-w-0">
                                    <h3 className="font-display font-black text-xl tracking-tight text-slate-900 leading-tight">
                                        Take a breather, come back later.
                                    </h3>
                                    <p className="text-sm text-slate-600 mt-1">
                                        Freeze billing and access for 1, 2, or 3 months. Your XP,
                                        beacons and referrals are preserved. Atlas auto-resumes on
                                        the return date — no reminder needed.
                                    </p>
                                </div>
                            </div>
                            <div className="grid grid-cols-3 gap-2">
                                {[1, 2, 3].map((m) => (
                                    <button
                                        key={m}
                                        disabled={billingBusy}
                                        onClick={() => doPause(m)}
                                        className="arcade-btn"
                                        data-variant="primary"
                                        data-testid={`pause-months-${m}`}
                                    >
                                        {m} {m === 1 ? "month" : "months"}
                                    </button>
                                ))}
                            </div>
                            <button
                                onClick={() => setPauseOpen(false)}
                                className="text-sm text-slate-500 hover:text-slate-900 underline font-display font-bold"
                                data-testid="pause-cancel"
                            >
                                ← Back
                            </button>
                        </div>
                    )}

                    {confirmCancel && (
                        <div className="rounded-2xl border border-amber-200 bg-gradient-to-br from-amber-50 to-white p-5 space-y-4" data-testid="cancel-confirm-block">
                            {/* Rescue nudge — show earned value BEFORE letting them cancel */}
                            {(() => {
                                const daysLeft = (() => {
                                    if (!billing?.current_period_end) return 0;
                                    const diff = new Date(billing.current_period_end).getTime() - Date.now();
                                    return Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)));
                                })();
                                const referralDays = referrals?.days_of_credit || 0;
                                const referralCount = referrals?.referrals_made || 0;
                                const earnedMonths = Math.max(1, Math.floor(referralDays / 30));
                                const hasEarnings = referralCount > 0 || referralDays > 0;

                                return (
                                    <>
                                        <div className="flex items-start gap-3">
                                            <div className="w-10 h-10 rounded-2xl flex items-center justify-center flex-shrink-0" style={{ background: "#FEE2E2" }}>
                                                <Heart className="w-5 h-5" color="#DC2626" />
                                            </div>
                                            <div className="min-w-0">
                                                <h3 className="font-display font-black text-xl tracking-tight text-slate-900 leading-tight" data-testid="rescue-heading">
                                                    Before you go, look what you've built.
                                                </h3>
                                                <p className="text-sm text-slate-600 mt-1">
                                                    You're leaving mid-journey. Some things you'd lose access to:
                                                </p>
                                            </div>
                                        </div>

                                        {/* Earned-value stats grid */}
                                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2" data-testid="rescue-earned-grid">
                                            {daysLeft > 0 && (
                                                <div className="crystal rounded-xl p-3 bg-white/80" data-testid="rescue-days-left">
                                                    <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest font-bold text-slate-500">
                                                        <CreditCard className="w-3 h-3" /> Paid access
                                                    </div>
                                                    <div className="font-display font-black text-2xl tracking-tighter text-slate-900 mt-0.5">
                                                        {daysLeft}<span className="text-sm text-slate-500 ml-1">days left</span>
                                                    </div>
                                                </div>
                                            )}
                                            {referralCount > 0 && (
                                                <div className="crystal rounded-xl p-3 bg-white/80" data-testid="rescue-invites">
                                                    <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest font-bold text-slate-500">
                                                        <Gift className="w-3 h-3" /> Friends invited
                                                    </div>
                                                    <div className="font-display font-black text-2xl tracking-tighter text-slate-900 mt-0.5">
                                                        {referralCount}
                                                    </div>
                                                </div>
                                            )}
                                            {referralDays > 0 && (
                                                <div className="crystal rounded-xl p-3 bg-white/80" data-testid="rescue-credit">
                                                    <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest font-bold text-slate-500">
                                                        <Sparkles className="w-3 h-3" /> Referral credit
                                                    </div>
                                                    <div className="font-display font-black text-2xl tracking-tighter text-slate-900 mt-0.5">
                                                        {referralDays}<span className="text-sm text-slate-500 ml-1">days free</span>
                                                    </div>
                                                </div>
                                            )}
                                            {user.xp > 0 && (
                                                <div className="crystal rounded-xl p-3 bg-white/80" data-testid="rescue-xp">
                                                    <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest font-bold text-slate-500">
                                                        <Zap className="w-3 h-3" /> XP earned
                                                    </div>
                                                    <div className="font-display font-black text-2xl tracking-tighter text-slate-900 mt-0.5">
                                                        {user.xp}
                                                    </div>
                                                </div>
                                            )}
                                        </div>

                                        {hasEarnings && (
                                            <div className="text-sm text-slate-700 rounded-xl bg-white/80 p-3 leading-relaxed" data-testid="rescue-nudge">
                                                <b>You've already earned {earnedMonths} free month{earnedMonths !== 1 ? "s" : ""}</b> from your invites. Your subscription runs essentially free until then — cancelling now doesn't save you money, it just cuts you off from the disciplines you've been building in.
                                            </div>
                                        )}
                                        {!hasEarnings && (
                                            <div className="text-sm text-slate-700 rounded-xl bg-white/80 p-3 leading-relaxed" data-testid="rescue-invite-nudge">
                                                Before you go — <b>invite one friend and both of you get a free month.</b>{" "}
                                                <a href="/profile" className="text-humanities font-bold hover:underline">Grab your invite link →</a>{" "}
                                                It takes 10 seconds and turns your cancel into free access.
                                            </div>
                                        )}

                                        <div className="flex items-start gap-2 text-xs text-slate-600 rounded-xl bg-white/60 px-3 py-2">
                                            <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5 text-amber-700" />
                                            <div>
                                                If you still want to cancel, your access continues until{" "}
                                                <b>{billing?.current_period_end ? new Date(billing.current_period_end).toLocaleDateString() : "the end of this billing cycle"}</b>
                                                . Your XP, beacons, projects, referrals, and bounties stay put — you can always come back.
                                            </div>
                                        </div>

                                        {/* Cancel-reason survey — one question, five choices + Other.
                                            Answers land in the `cancel_reasons` collection and roll up
                                            into /admin/emails via the churn-reasons panel. */}
                                        <div className="rounded-xl bg-white/80 p-3 space-y-2" data-testid="cancel-reason-survey">
                                            <div className="font-display font-bold text-sm text-slate-900">
                                                Quick honest question — what's driving this?
                                            </div>
                                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                                                {[
                                                    { key: "too_expensive", label: "$2/mo is too expensive right now" },
                                                    { key: "not_using", label: "I'm not using it enough" },
                                                    { key: "missing_features", label: "Missing features I need" },
                                                    { key: "found_alternative", label: "Found a better alternative" },
                                                    { key: "just_testing", label: "Was just testing, done for now" },
                                                    { key: "other", label: "Other — I'll type it below" },
                                                ].map((opt) => (
                                                    <label
                                                        key={opt.key}
                                                        className={`flex items-center gap-2 rounded-lg px-2 py-1.5 text-sm cursor-pointer border transition-colors ${
                                                            cancelReason === opt.key
                                                                ? "bg-humanities/10 border-humanities text-humanities font-bold"
                                                                : "bg-white/70 border-slate-200 hover:border-slate-300"
                                                        }`}
                                                        data-testid={`cancel-reason-${opt.key}`}
                                                    >
                                                        <input
                                                            type="radio"
                                                            name="cancel-reason"
                                                            value={opt.key}
                                                            checked={cancelReason === opt.key}
                                                            onChange={() => setCancelReason(opt.key)}
                                                            className="accent-humanities"
                                                        />
                                                        <span className="leading-tight">{opt.label}</span>
                                                    </label>
                                                ))}
                                            </div>
                                            {cancelReason === "other" && (
                                                <textarea
                                                    value={cancelDetail}
                                                    onChange={(e) => setCancelDetail(e.target.value)}
                                                    placeholder="What's the reason? (optional, helps us fix it)"
                                                    className="field-input w-full text-sm mt-1"
                                                    rows={2}
                                                    maxLength={500}
                                                    data-testid="cancel-reason-detail"
                                                />
                                            )}
                                        </div>

                                        <div className="flex flex-wrap gap-2 pt-1">
                                            <button
                                                onClick={() => { setConfirmCancel(false); setCancelReason(null); setCancelDetail(""); }}
                                                disabled={billingBusy}
                                                className="arcade-btn"
                                                data-variant="primary"
                                                data-testid="cancel-subscription-back"
                                            >
                                                <Heart className="w-4 h-4" /> Never mind — keep exploring
                                            </button>
                                            <button
                                                onClick={doCancel}
                                                disabled={billingBusy || !cancelReason}
                                                className="text-sm text-slate-500 hover:text-slate-900 underline font-display font-bold px-3 disabled:opacity-40 disabled:cursor-not-allowed"
                                                data-testid="cancel-subscription-confirm"
                                                title={!cancelReason ? "Pick a reason above to enable this button" : undefined}
                                            >
                                                {billingBusy ? "Cancelling…" : "I'm sure, cancel my subscription"}
                                            </button>
                                        </div>
                                    </>
                                );
                            })()}
                        </div>
                    )}

                    {billing?.cancel_at_period_end && (
                        <button
                            onClick={doResume}
                            disabled={billingBusy}
                            className="arcade-btn"
                            data-variant="primary"
                            data-testid="resume-subscription"
                        >
                            <RotateCcw className="w-4 h-4" /> Resume subscription
                        </button>
                    )}
                </motion.div>

                {/* --- Organization / Recruitment section --- */}
                <motion.form
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    onSubmit={submitOrg}
                    className="crystal rounded-3xl p-6 sm:p-8 mt-8 space-y-5"
                    data-testid="org-convert-form"
                >
                    <div className="flex flex-wrap items-center gap-2">
                        <p className="chip" style={{ background: BRANCH_META.business.soft, color: BRANCH_META.business.text }}>
                            <Building2 className="w-3 h-3" /> Organization
                        </p>
                        {user.is_organization && user.org_verified && <VerifiedOrgBadge />}
                        {user.is_organization && !user.org_verified && (
                            <span className="chip bg-amber-100 text-amber-800">Unverified</span>
                        )}
                    </div>
                    <h2 className="font-display font-black text-2xl sm:text-3xl tracking-tighter text-slate-900 leading-none">
                        {user.is_organization ? "Manage your organization" : "Convert to an Organization"}
                    </h2>
                    <p className="text-sm text-slate-600">
                        {user.is_organization
                            ? "Update your public organization details. To earn the Verified badge, your login email domain must match the org domain."
                            : "Post recruitment listings for talent in any discipline. Auto-verified when your login email domain matches your org domain."}
                    </p>

                    <Field
                        icon={<Building2 className="w-4 h-4" />}
                        label="Organization name"
                        value={orgForm.org_name}
                        onChange={(v) => setOrgForm({ ...orgForm, org_name: v })}
                        testid="org-name"
                        maxLength={120}
                        required
                        placeholder="Acme Inc"
                    />

                    <Field
                        icon={<Mail className="w-4 h-4" />}
                        label="Org email domain"
                        value={orgForm.org_domain}
                        onChange={(v) => setOrgForm({ ...orgForm, org_domain: v })}
                        testid="org-domain"
                        maxLength={120}
                        required
                        placeholder="acme.com"
                        hint={
                            (orgForm.org_domain || "").toLowerCase().trim().replace(/^@/, "") ===
                            (user.email || "").split("@")[1]
                                ? "✓ Matches your login email — you'll be Verified on save."
                                : `Your login email is @${(user.email || "").split("@")[1] || ""}. Use that domain to be auto-verified.`
                        }
                    />

                    <Field
                        icon={<Globe className="w-4 h-4" />}
                        label="Org website"
                        value={orgForm.org_website}
                        onChange={(v) => setOrgForm({ ...orgForm, org_website: v })}
                        testid="org-website"
                        maxLength={240}
                        placeholder="https://acme.com"
                    />

                    <Field
                        icon={<ScrollText className="w-4 h-4" />}
                        label="Short description (optional)"
                        value={orgForm.org_description}
                        onChange={(v) => setOrgForm({ ...orgForm, org_description: v })}
                        testid="org-description"
                        maxLength={600}
                        textarea
                        placeholder="What does your org do? Who are you looking for?"
                    />

                    {orgError && (
                        <div className="text-sm text-white bg-sciences px-3 py-2 rounded-xl" data-testid="org-error">
                            {orgError}
                        </div>
                    )}

                    <div className="flex flex-wrap items-center justify-between gap-3 pt-2">
                        {user.is_organization ? (
                            <Link
                                to="/jobs/new"
                                className="arcade-btn"
                                data-variant="ghost"
                                data-testid="post-role-link"
                            >
                                <Briefcase className="w-4 h-4" /> Post a role ($5)
                            </Link>
                        ) : (
                            <span className="text-xs text-slate-500">One-way switch (for now) — you'll still keep all your XP, projects, and beacons.</span>
                        )}
                        <div className="flex items-center gap-3">
                            {orgSaved && (
                                <span className="text-sm text-trades font-display font-bold flex items-center gap-1">
                                    <Check className="w-4 h-4" /> Saved
                                </span>
                            )}
                            <button
                                disabled={orgBusy}
                                className="arcade-btn"
                                data-variant="primary"
                                data-testid="org-save"
                                type="submit"
                            >
                                <Save className="w-4 h-4" />
                                {orgBusy ? "Saving…" : user.is_organization ? "Save org" : "Convert to Organization"}
                            </button>
                        </div>
                    </div>
                </motion.form>
            </div>
        </div>
    );
}

function Field({ icon, label, value, onChange, testid, hint, textarea, ...rest }) {
    return (
        <label className="block">
            <div className="text-xs uppercase tracking-widest font-bold text-slate-400 mb-1.5 flex items-center gap-1.5">
                {icon} {label}
            </div>
            {textarea ? (
                <textarea
                    className="field-input min-h-[92px] resize-y"
                    value={value ?? ""}
                    onChange={(e) => onChange && onChange(e.target.value)}
                    data-testid={testid}
                    {...rest}
                />
            ) : (
                <input
                    className="field-input"
                    value={value ?? ""}
                    onChange={(e) => onChange && onChange(e.target.value)}
                    data-testid={testid}
                    {...rest}
                />
            )}
            {hint && <p className="text-[11px] text-slate-500 mt-1">{hint}</p>}
        </label>
    );
}
